import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  Icon,
  Skeleton,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { completedParse, resumeByBatch } from 'services/api/modules/recruitment/external-talent'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import AdxDropdown from 'components/custom/AdxDropdown'
import AdxMenuItem from 'components/custom/AdxMenuItem'
import FirstPageRoundedIcon from '@mui/icons-material/FirstPageRounded'
import LastPageRoundedIcon from '@mui/icons-material/LastPageRounded'
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const CompletedTab = ({ isOpen }) => {
  const [expanded, setExpanded] = useState(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  const { data, isLoading } = useQuery(['completedParse', rowsPerPage, page], async () => {
    if (isOpen) {
      const { data } = await completedParse(rowsPerPage, page + 1)
      return data
    }
  })

  const isFirstPage = page === 0
  const isLastPage = page === data?.last_page - 1

  const {
    data: resumeData,
    isLoading: resumeLoading,
    isFetching: resumeFetching,
    refetch,
  } = useQuery('resumeByBatch', async () => {
    if (expanded) {
      const { data } = await resumeByBatch(expanded)
      return data
    }
  })

  const onRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value)
    setPage(0)
  }

  const dateFormatter = (date) => {
    const parsedDate = new Date(date)
    const formattedDate = format(parsedDate, 'dd/MM/yyyy, hh:mm a')
    return formattedDate
  }

  const handleAccordion = (batch) => {
    setExpanded((prev) => (prev === batch ? null : batch))
  }

  useEffect(() => {
    if (expanded) {
      refetch()
    }
  }, [expanded])

  return (
    <Box sx={{ position: 'relative' }}>
      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '50vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress sx={{ color: 'alternate' }} />
        </Box>
      ) : data?.data?.length > 0 ? (
        <Box sx={{ height: 'calc(100vh - 175px)', overflowY: 'scroll' }}>
          {data?.data?.map((i, index) => {
            const isExpanded = expanded === i.batch

            return (
              <Accordion
                key={index}
                disableGutters={true}
                onClick={() => handleAccordion(i.batch)}
                expanded={isExpanded}
                sx={{
                  boxShadow: 'none',
                  borderBottom: index === data?.data?.length - 1 ? '' : '1px solid #E5E5E5',
                }}
              >
                <AccordionSummary sx={{ padding: isExpanded ? '12px 24px 8px' : '12px 24px' }}>
                  <Box
                    sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Typography fontWeight="bold">Batch {i.batch}</Typography>
                      <Typography>Uploaded on {dateFormatter(i.upload_date)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="light-gray-text.main">Total upload</Typography>
                        <Typography>{i.count}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="light-gray-text.main">AI analysis completed</Typography>
                        <Typography>{i.parsing_completed}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography color="light-gray-text.main">AI analysis failed</Typography>
                        <Typography>{i.parsing_failed}</Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'alternate.main',
                        cursor: 'pointer',
                      }}
                    >
                      <Typography fontWeight="bold">
                        {isExpanded ? 'Hide' : 'Show'} resume
                      </Typography>
                      <ExpandMoreRoundedIcon sx={{ rotate: isExpanded ? '180deg' : '' }} />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: '0 24px 24px' }}>
                  {resumeLoading || resumeFetching ? (
                    <Skeleton variant="text" width="100%" height={50} />
                  ) : (
                    <Box sx={{ border: '1px solid #E5E5E5', borderRadius: '4.5px' }}>
                      {resumeData?.map((i, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px',
                            borderTop: index === 0 ? '' : '1px solid #E5E5E5',
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                borderRadius: '100%',
                                backgroundColor: 'default-background.main',
                                width: '35px',
                                height: '35px',
                              }}
                            >
                              <Icon sx={{ color: 'alternate.main', fontSize: '20px' }}>
                                description
                              </Icon>
                            </Box>
                            <Typography>{i.original_filename}</Typography>
                          </Box>
                          {i.status === 2 ? (
                            <Box
                              sx={{
                                width: '100px',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'light-gray-text.main',
                                gap: '8px',
                              }}
                            >
                              <CheckCircleRoundedIcon sx={{ color: 'positive.main' }} />
                              <Typography>Parsed</Typography>
                            </Box>
                          ) : i.status === 4 ? (
                            <Box
                              sx={{
                                width: '100px',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'light-gray-text.main',
                                gap: '8px',
                              }}
                            >
                              <CancelRoundedIcon sx={{ color: 'error.main' }} />
                              <Typography>Failed</Typography>
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  )}
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>
      ) : (
        <AdxEmptyStateV2
          icon={
            <Box sx={{ width: '36px', height: '36px' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"
                />
              </svg>
            </Box>
          }
          title="No data"
        />
      )}
      {!isLoading && data?.data?.length > 0 && (
        <Box
          sx={{
            padding: '16px 24px 20px',
            position: 'sticky',
            bottom: 0,
            left: 0,
            backgroundColor: 'white.main',
            borderTop: '1px solid #E5E5E5',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <AdxDropdown
              sxDropdown={{ width: '64px', height: '35px' }}
              defaultValue={10}
              value={rowsPerPage}
              onChange={onRowsPerPageChange}
            >
              {[5, 10, 20, 50].map((i, index) => (
                <AdxMenuItem key={index} value={i}>
                  {i}
                </AdxMenuItem>
              ))}
            </AdxDropdown>
            <Typography sx={{ color: 'light-gray-text.main' }}>
              {data?.from}-{data?.to} of {data?.total} batch
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              onClick={!isFirstPage ? () => setPage(0) : () => {}}
              sx={{
                color: isFirstPage ? 'gray-inactive-button.main' : 'light-gray-text.main',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !isFirstPage ? 'pointer' : '',
              }}
            >
              <FirstPageRoundedIcon />
            </Box>
            <Box
              onClick={!isFirstPage ? () => setPage((prev) => prev - 1) : () => {}}
              sx={{
                color: isFirstPage ? 'gray-inactive-button.main' : 'light-gray-text.main',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !isFirstPage ? 'pointer' : '',
              }}
            >
              <ChevronLeftRoundedIcon />
            </Box>
            <Box
              onClick={!isLastPage ? () => setPage((prev) => prev + 1) : () => {}}
              sx={{
                color: isLastPage ? 'gray-inactive-button.main' : 'light-gray-text.main',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !isLastPage ? 'pointer' : '',
              }}
            >
              <ChevronRightRoundedIcon />
            </Box>
            <Box
              onClick={!isLastPage ? () => setPage(data?.last_page - 1) : () => {}}
              sx={{
                color: isLastPage ? 'gray-inactive-button.main' : 'light-gray-text.main',
                width: '35px',
                height: '35px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: !isLastPage ? 'pointer' : '',
              }}
            >
              <LastPageRoundedIcon />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default CompletedTab
